/**
 * AILatestRedirect — resolves /ai/latest/:personality to the user's
 * currently-surviving review for that personality + replaces the
 * history entry with /ai/<id>. Used by notification deep-links: only
 * the latest review per personality is persisted, so notifications
 * can't carry a specific review id (it gets pruned the moment a
 * newer review for the same personality completes).
 *
 * Falls back to /ai (the picker) if nothing exists for that
 * personality OR if the backend lookup errors out.
 */
import { useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { aireviewApi, APIError } from '@/lib/api';
import { Spinner } from '@/components';

export function AILatestRedirect() {
  const { personality } = useParams<{ personality: string }>();
  const [target, setTarget] = useState<string | null>(null);

  useEffect(() => {
    if (!personality) {
      setTarget('/ai');
      return;
    }
    aireviewApi
      .latestByPersonality(personality)
      .then((r) => setTarget(`/ai/${r.id}`))
      .catch((e) => {
        // 404 → no review yet for this personality → picker.
        // Anything else (network, 401, etc.) also goes to the picker;
        // it'll surface the right error in its own UI.
        void (e instanceof APIError);
        setTarget('/ai');
      });
  }, [personality]);

  if (!target) {
    return (
      <main style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <Spinner size={24} color="var(--ink-3)" />
      </main>
    );
  }
  return <Navigate to={target} replace />;
}
